import React, { useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import { useDepositSettings } from '../../../components/settings/deposits/useDepositSettings';
import { formatEmployeeNameForClient } from '../../storefront-shared/utils/employeeNameUtils';
import { STOREFRONT_GATEWAY_IDS } from './gatewayIds';
import { startStorefrontDeluxeCheckout, chargeStorefrontDeluxeCheckout } from './storefront-deluxe/startStorefrontDeluxeCheckout';
import DeluxeHostedPaymentForm from '../../../deluxe/components/DeluxeHostedPaymentForm';
import DeluxeBookingPaymentTabs from '../../../deluxe/embedded/wallets/payment-method-tabs/DeluxeBookingPaymentTabs';
import { ensureStorefrontClientForPayment } from './ensureStorefrontClientForPayment';
import { resolveStorefrontChosenEmployee } from './resolveStorefrontChosenEmployee';
import AvailabilityService from '../../storefront-client-portal/bundle/components/booking/services/AvailabilityService';
import { getDoubleBookingSettings } from '../../../shared/booking/double-booking/employeeSlotAvailability';
import { computeStorefrontBookingTotal } from './resolveStorefrontPaymentGateways';
import { resolveEffectiveCompanyHours } from '../../../locations/resolveLocationHours';
import DepositCancellationPolicy, {
  hasCancellationPolicyContent,
} from '../../shared/deposit/DepositCancellationPolicy';
import './storefront-booking-payment.css';

const formatMoney = (n) => {
  const num = Number(n) || 0;
  return num.toFixed(2);
};

const checkoutStarts = new Map();

const storefrontCheckoutKey = ({ companyId, serviceId, time, dueNow }) =>
  [companyId || '', serviceId || '', time instanceof Date ? time.getTime() : time || '', Number(dueNow) || 0].join('|');

function getOrStartCheckout(key, start) {
  const existing = checkoutStarts.get(key);
  if (existing) return existing;
  const promise = Promise.resolve()
    .then(start)
    .then((checkout) => {
      if (!checkout) {
        checkoutStarts.delete(key);
        throw new Error('Checkout did not start');
      }
      return checkout;
    })
    .catch((error) => {
      checkoutStarts.delete(key);
      throw error;
    });
  checkoutStarts.set(key, promise);
  return promise;
}

export default function StorefrontPaymentStep({
  companyData,
  companyUrl,
  serviceSlug,
  service,
  staff,
  availableStaff,
  date,
  time,
  selectedAddons,
  selectedPackage,
  customerInfo,
  isSignedInClient,
  storefrontClient,
  enabledGateways,
  getTotalDurationMinutes,
  selectedLocation = null,
  onBack,
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [deluxeCheckout, setDeluxeCheckout] = useState(null);
  const [selectedMethod, setSelectedMethod] = useState(() => {
    const deluxe = enabledGateways.find((g) => g.id === STOREFRONT_GATEWAY_IDS.deluxe);
    if (deluxe) return STOREFRONT_GATEWAY_IDS.deluxe;
    return enabledGateways[0]?.id || null;
  });

  const { settings: depositSettings, calculateServiceDeposit } = useDepositSettings(companyData);

  const serviceForDeposit = useMemo(
    () =>
      service
        ? {
            ...service,
            selectedPackage,
            selectedAddons,
          }
        : null,
    [service, selectedPackage, selectedAddons]
  );

  const total = useMemo(
    () => computeStorefrontBookingTotal(service, selectedPackage, selectedAddons),
    [service, selectedPackage, selectedAddons]
  );

  const depositAmountRaw = useMemo(() => {
    if (!serviceForDeposit || !depositSettings?.globalEnabled) {
      return 0;
    }
    try {
      const amount = calculateServiceDeposit(serviceForDeposit);
      return amount > 0 ? amount : 0;
    } catch (e) {
      console.error('[StorefrontPayment] deposit calculation failed', e);
      return 0;
    }
  }, [serviceForDeposit, depositSettings, calculateServiceDeposit]);

  const depositDue = useMemo(() => {
    if (!depositSettings?.globalEnabled || depositAmountRaw <= 0) {
      return 0;
    }
    return Math.min(depositAmountRaw, total);
  }, [depositSettings, depositAmountRaw, total]);

  const payDeposit = depositDue > 0.01;
  const dueNow = payDeposit ? depositDue : total;
  const checkoutGateways = useMemo(
    () => (enabledGateways || []).filter((g) => g.supportsStorefrontCheckout),
    [enabledGateways]
  );

  const chosenEmployee = useMemo(
    () =>
      resolveStorefrontChosenEmployee({
        staff,
        availableStaff,
        date,
        time,
        companyHours: resolveEffectiveCompanyHours({
          location: selectedLocation,
          companyData,
          locationId: selectedLocation?.id,
        }),
        getTotalDurationMinutes,
      }),
    [staff, availableStaff, date, time, companyData, selectedLocation, getTotalDurationMinutes]
  );

  const durationMinutes = useMemo(() => {
    if (typeof getTotalDurationMinutes !== 'function') return 0;
    return getTotalDurationMinutes() || 0;
  }, [getTotalDurationMinutes, service, selectedPackage, selectedAddons]);

  const appointmentWhen = useMemo(() => {
    if (!date || !time) return null;
    return `${format(date, 'EEEE, MMMM d, yyyy')} · ${format(time, 'h:mm a')}`;
  }, [date, time]);

  const staffSummaryLabel = useMemo(() => {
    if (!staff) return '—';
    if (staff.isAnyAvailable) {
      const assignee = chosenEmployee
        ? formatEmployeeNameForClient(chosenEmployee)
        : null;
      return assignee ? `Any available · ${assignee}` : 'Any available';
    }
    return formatEmployeeNameForClient(staff);
  }, [staff, chosenEmployee]);

  const contactSummary = useMemo(() => {
    const name = (
      isSignedInClient
        ? storefrontClient?.name || customerInfo?.name
        : customerInfo?.name
    )?.trim();
    const email = (
      isSignedInClient
        ? storefrontClient?.email || customerInfo?.email
        : customerInfo?.email
    )?.trim();
    const phone = (
      isSignedInClient
        ? storefrontClient?.phone || customerInfo?.phone
        : customerInfo?.phone
    )?.trim();
    return { name: name || '', email: email || '', phone: phone || '' };
  }, [isSignedInClient, storefrontClient, customerInfo]);

  const createDeluxeCheckout = async () => {
    if (!chosenEmployee) {
      throw new Error('Could not determine the staff member for this booking. Go back and pick a time again.');
    }

    const conflictSettings = getDoubleBookingSettings(companyData);
    const serviceDuration = selectedPackage?.duration || service.duration || 0;
    const serviceBuffer = selectedPackage?.duration
      ? (selectedPackage.bufferTime || 0)
      : (service.bufferTime || 0);

    if (staff?.isAnyAvailable) {
      const rosterIds = (availableStaff || [])
        .filter((member) => !member.isAnyAvailable)
        .map((member) => member.id);
      const { isAvailable } = await AvailabilityService.checkAnyEmployeeAvailability({
        companyId: companyData.id,
        employeeIds: rosterIds,
        startTime: time,
        duration: serviceDuration,
        bufferTime: serviceBuffer,
        addons: selectedAddons || [],
        ...conflictSettings,
      });
      if (!isAvailable) {
        throw new Error('This time slot is no longer available. Please choose a different time.');
      }
    } else {
      const { isAvailable } = await AvailabilityService.checkEmployeeAvailability({
        companyId: companyData.id,
        employeeId: chosenEmployee.id,
        startTime: time,
        duration: serviceDuration,
        bufferTime: serviceBuffer,
        addons: selectedAddons || [],
        ...conflictSettings,
      });
      if (!isAvailable) {
        throw new Error(
          'This time slot is no longer available for the selected staff member. Please choose a different time.'
        );
      }
    }

    const gateway = enabledGateways.find((g) => g.id === selectedMethod);
    if (!gateway?.supportsStorefrontCheckout) {
      throw new Error(gateway?.storefrontMessage || 'This payment option is not available on the website yet.');
    }

    const client = await ensureStorefrontClientForPayment({
      companyId: companyData.id,
      customerInfo,
      isSignedInClient,
      storefrontClient,
    });

    let totalDuration = selectedPackage?.duration || service.duration || 0;
    if (selectedPackage?.duration) {
      totalDuration += selectedPackage.bufferTime || 0;
    }
    (selectedAddons || []).forEach((addon) => {
      totalDuration += addon.duration || 0;
      totalDuration += addon.bufferTime || 0;
    });
    const durationMinutes = totalDuration + (service.bufferTime || 0);

    return startStorefrontDeluxeCheckout({
      companyId: companyData.id,
      companyUrl,
      serviceSlug,
      service,
      selectedPackage,
      selectedAddons,
      appointmentDateTime: time,
      durationMinutes,
      chosenEmployee,
      clientId: client.clientId,
      clientName: client.clientName,
      clientEmail: client.clientEmail,
      clientPhone: client.clientPhone,
      totalPrice: total,
      depositAmount: depositDue,
      checkoutDeposit: payDeposit,
      selectedLocation,
    });
  };

  const checkoutKey = storefrontCheckoutKey({
    companyId: companyData?.id,
    serviceId: service?.id,
    time,
    dueNow,
  });
  const canAutoStartDeluxe =
    !deluxeCheckout &&
    selectedMethod === STOREFRONT_GATEWAY_IDS.deluxe &&
    dueNow > 0.01 &&
    Boolean(chosenEmployee);

  const applyCheckout = async (forceRestart = false) => {
    setError(null);
    if (forceRestart) checkoutStarts.delete(checkoutKey);
    setLoading(true);
    try {
      const checkout = await getOrStartCheckout(checkoutKey, createDeluxeCheckout);
      setDeluxeCheckout(checkout);
      return true;
    } catch (e) {
      console.error('[StorefrontPayment] Deluxe', e);
      setError(e.message || 'Payment could not be started. Try again or contact the business.');
      return false;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!canAutoStartDeluxe) return undefined;
    let cancelled = false;
    void applyCheckout().then(() => {
      if (cancelled) return;
    });
    return () => {
      cancelled = true;
    };
    // Start checkout once when the summary page is ready so the card form is visible immediately.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canAutoStartDeluxe, checkoutKey]);

  return (
    <div className="booking-step storefront-payment-step booking-step--payment">
      <h3>Review and pay</h3>
      <p className="storefront-payment-step__intro">
        Review your appointment and enter your card details below. The business receives the funds through
        their connected processor.
      </p>

      <div className="storefront-payment-step__summary">
        <h4 className="storefront-payment-step__summary-title">Appointment summary</h4>
        <dl className="storefront-payment-step__summary-list">
          {service?.name ? (
            <div className="storefront-payment-step__summary-row">
              <dt>Service</dt>
              <dd>{service.name}</dd>
            </div>
          ) : null}
          {selectedPackage?.name ? (
            <div className="storefront-payment-step__summary-row">
              <dt>Package</dt>
              <dd>{selectedPackage.name}</dd>
            </div>
          ) : null}
          {selectedAddons?.length ? (
            <div className="storefront-payment-step__summary-row storefront-payment-step__summary-row--addons">
              <dt>Add-ons</dt>
              <dd>
                <ul className="storefront-payment-step__addon-list">
                  {selectedAddons.map((a) => (
                    <li key={a.id || a.name}>{a.name}</li>
                  ))}
                </ul>
              </dd>
            </div>
          ) : null}
          {durationMinutes > 0 ? (
            <div className="storefront-payment-step__summary-row">
              <dt>Duration</dt>
              <dd>{durationMinutes} minutes</dd>
            </div>
          ) : null}
          {appointmentWhen ? (
            <div className="storefront-payment-step__summary-row">
              <dt>Date & time</dt>
              <dd>{appointmentWhen}</dd>
            </div>
          ) : null}
          <div className="storefront-payment-step__summary-row">
            <dt>Staff</dt>
            <dd>{staffSummaryLabel}</dd>
          </div>
          {contactSummary.name || contactSummary.email || contactSummary.phone ? (
            <div className="storefront-payment-step__summary-row">
              <dt>Booked for</dt>
              <dd className="storefront-payment-step__summary-contact">
                {contactSummary.name ? <span>{contactSummary.name}</span> : null}
                {contactSummary.email ? (
                  <span className="storefront-payment-step__summary-email">{contactSummary.email}</span>
                ) : null}
                {contactSummary.phone ? (
                  <span className="storefront-payment-step__summary-email">{contactSummary.phone}</span>
                ) : null}
              </dd>
            </div>
          ) : null}
        </dl>
      </div>

      {hasCancellationPolicyContent(depositSettings?.cancellationPolicy) && (
        <section
          className="storefront-payment-step__policy-section"
          aria-label="Cancellation and refund policy"
        >
          <DepositCancellationPolicy cancellationPolicy={depositSettings?.cancellationPolicy} />
        </section>
      )}

      <h4 className="storefront-payment-step__section-label">Amount</h4>

      <div className="storefront-payment-step__totals">
        {payDeposit ? (
          <>
            <div className="storefront-payment-step__total storefront-payment-step__total--sub">
              <span>Appointment total</span>
              <strong>${formatMoney(total)}</strong>
            </div>
            <div className="storefront-payment-step__total storefront-payment-step__total--sub">
              <span>Balance after deposit</span>
              <strong>${formatMoney(Math.max(0, total - depositDue))}</strong>
            </div>
            <div className="storefront-payment-step__total storefront-payment-step__total--now">
              <span>Due now (deposit)</span>
              <strong>${formatMoney(dueNow)}</strong>
            </div>
          </>
        ) : (
          <div className="storefront-payment-step__total">
            <span>Total due now</span>
            <strong>${formatMoney(total)}</strong>
          </div>
        )}
      </div>

      {checkoutGateways.length > 1 ? (
        <>
          <h4 className="storefront-payment-step__section-label">Payment method</h4>
          <div className="storefront-payment-step__methods" role="radiogroup" aria-label="Payment method">
            {checkoutGateways.map((g) => (
              <label
                key={g.id}
                className={`storefront-payment-step__method ${
                  selectedMethod === g.id ? 'storefront-payment-step__method--active' : ''
                }`}
              >
                <input
                  type="radio"
                  name="storefront-gateway"
                  value={g.id}
                  checked={selectedMethod === g.id}
                  onChange={() => setSelectedMethod(g.id)}
                />
                <span>{g.label}</span>
              </label>
            ))}
          </div>
        </>
      ) : null}

      {error ? <div className="error-message storefront-payment-step__error">{error}</div> : null}

      {deluxeCheckout ? (
        <DeluxeBookingPaymentTabs
          wallets={deluxeCheckout.wallets}
          disabled={loading}
          onWalletSuccess={async ({ paymentId }) => {
            try {
              setLoading(true);
              await chargeStorefrontDeluxeCheckout({
                companyId: companyData.id,
                appointmentId: deluxeCheckout.appointmentId,
                paymentId,
              });
              window.location.assign(
                `/book/${encodeURIComponent(companyUrl)}/booking/confirmed/${deluxeCheckout.appointmentId}`
              );
            } catch (e) {
              setError(e.message || 'Wallet payment failed. Please try again.');
              setLoading(false);
            }
          }}
          onWalletError={(e) => setError(e?.message || 'Wallet payment failed')}
          cardForm={(
            <DeluxeHostedPaymentForm
              hpfToken={deluxeCheckout.hpfToken}
              scriptUrl={deluxeCheckout.scriptUrl}
              mode="cryptogram"
              amount={deluxeCheckout.amount || dueNow}
              disabled={loading}
              onSuccess={async ({ cryptogram }) => {
                try {
                  setLoading(true);
                  await chargeStorefrontDeluxeCheckout({
                    companyId: companyData.id,
                    appointmentId: deluxeCheckout.appointmentId,
                    cryptogram,
                  });
                  window.location.assign(
                    `/book/${encodeURIComponent(companyUrl)}/booking/confirmed/${deluxeCheckout.appointmentId}`
                  );
                } catch (e) {
                  setError(e.message || 'Payment failed. Please try again.');
                  setLoading(false);
                }
              }}
              onError={(e) => setError(e?.message || 'Payment form failed')}
            />
          )}
        />
      ) : (
        <p className="storefront-payment-step__preparing">
          {loading || canAutoStartDeluxe
            ? 'Loading secure card form…'
            : 'Payment is unavailable for this booking.'}
        </p>
      )}

      <div className="button-group">
        <button type="button" className="btn-secondary" onClick={onBack} disabled={loading}>
          Back
        </button>
        {error && !deluxeCheckout ? (
          <button type="button" className="btn-primary" onClick={() => applyCheckout(true)} disabled={loading}>
            {loading ? 'Starting checkout…' : 'Try again'}
          </button>
        ) : null}
      </div>
    </div>
  );
}
